import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router';

import type { BuyerRequest, BuyerRequestUpdate } from '../api/types';
import { EmptyState } from '../components/EmptyState';
import { ErrorMessage } from '../components/ErrorMessage';
import { MutationError } from '../components/MutationError';
import { fmtDate } from '../ops/format';
import { useProject } from '../projects/context';
import { projectKeys, useProjectApi } from '../projects/useProjectApi';
import { BuyerMatches } from './BuyerMatches';
import { buyerProfileChips, propertyTypeLabels, purposeLabels } from './buyerFormat';

const tabs = [
  { key: '', label: 'Offen' },
  { key: 'DONE', label: 'Erledigt' },
  { key: 'CANCELED', label: 'Kein Bedarf' },
] as const;

const statusBadge: Record<BuyerRequest['status'], { label: string; tone: string }> = {
  OPEN: { label: 'Neu', tone: 'badge-warn' },
  IN_PROGRESS: { label: 'In Bearbeitung', tone: 'badge-info' },
  DONE: { label: 'Erledigt', tone: 'badge-ok' },
  CANCELED: { label: 'Kein Bedarf', tone: 'badge-muted' },
};

// BuyerRequestsPage: Kaufgesuche (Dominik 14.10.2026) – Eigentümer, die in der Akquise sagen, dass sie selbst eine Immobilie
// suchen. Der Makler des Gebiets sieht seine, Büro und Akquise alle; Ergebnis und Suchprofil werden hier eingetragen.
export function BuyerRequestsPage({ embedded = false }: { embedded?: boolean }) {
  const { project } = useProject();
  const papi = useProjectApi();
  const [params, setParams] = useSearchParams();
  const status = params.get('status') ?? '';
  const highlight = params.get('id');
  const list = useQuery({
    queryKey: projectKeys.buyerRequests(project.id, status),
    queryFn: ({ signal }) => papi.buyerRequests(status || undefined, signal),
  });
  return (
    <div className={embedded ? 'embedded-page' : 'page'}>
      {!embedded && (
        <header className="page-header">
          <h1>Kaufgesuche</h1>
        </header>
      )}
      <p className="muted">Eigentümer, die selbst eine Immobilie suchen – Kontakt aufnehmen, passende Objekte anbieten und das Ergebnis eintragen.</p>
      <div className="tabs" role="tablist" aria-label="Kaufgesuche">
        {tabs.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={status === t.key}
            className={status === t.key ? 'tab active' : 'tab'}
            onClick={() => setParams((p) => (t.key ? p.set('status', t.key) : p.delete('status'), p.delete('id'), p), { replace: true })}
          >
            {t.label}
          </button>
        ))}
      </div>
      {list.isPending ? (
        <p className="muted">Wird geladen …</p>
      ) : list.isError ? (
        <ErrorMessage error={list.error} onRetry={() => void list.refetch()} />
      ) : list.data.items.length === 0 ? (
        <EmptyState title={status ? 'Keine Kaufgesuche' : 'Keine offenen Kaufgesuche'}>
          Neue Kaufgesuche entstehen in der Akquise mit dem Ergebnis „Sucht Immobilie“.
        </EmptyState>
      ) : (
        <div className="buyer-list">
          {list.data.items.map((b) => (
            <BuyerCard key={b.id} b={b} highlighted={b.id === highlight} />
          ))}
        </div>
      )}
    </div>
  );
}

function BuyerCard({ b, highlighted }: { b: BuyerRequest; highlighted: boolean }) {
  const { project, can } = useProject();
  const papi = useProjectApi();
  const queryClient = useQueryClient();
  const [closing, setClosing] = useState<'' | 'DONE' | 'CANCELED'>('');
  const [result, setResult] = useState('');
  const [editing, setEditing] = useState(false);
  const save = useMutation({
    mutationFn: (body: BuyerRequestUpdate) => papi.updateBuyerRequest(b.id, body),
    onSuccess: () => {
      setClosing('');
      setResult('');
      setEditing(false);
      void queryClient.invalidateQueries({ queryKey: ['projects', project.id, 'buyer-requests'] });
    },
  });
  const base = `/p/${project.key}`;
  const badge = statusBadge[b.status];
  const open = b.status === 'OPEN' || b.status === 'IN_PROGRESS';
  const canEdit = can('lead.edit');
  return (
    <section className={highlighted ? 'card buyer-card highlighted' : 'card buyer-card'} aria-label={`Kaufgesuch ${b.lead_name}`}>
      <div className="buyer-card-head">
        <h2>
          {b.lead_id ? <Link to={`${base}/leads/${b.lead_id}`}>{b.lead_name || 'Lead'}</Link> : b.lead_name}
          {b.city && <span className="muted"> · {b.city}</span>}
        </h2>
        <span className={`badge ${badge.tone}`}>{badge.label}</span>
      </div>
      <ul className="tags" aria-label="Suchprofil">
        {buyerProfileChips(b).map((c) => (
          <li key={c} className="tag">
            {c}
          </li>
        ))}
      </ul>
      <blockquote className="buyer-wish">{b.wish}</blockquote>
      <dl className="buyer-facts">
        {b.phone && (
          <>
            <dt>Telefon</dt>
            <dd>
              <a href={`tel:${b.phone}`}>{b.phone}</a>
            </dd>
          </>
        )}
        {b.email && (
          <>
            <dt>E-Mail</dt>
            <dd>
              <a href={`mailto:${b.email}`}>{b.email}</a>
            </dd>
          </>
        )}
        <dt>Makler</dt>
        <dd>{b.broker_name || 'noch keiner (Gebiet ohne Makler)'}</dd>
        <dt>Aufgenommen</dt>
        <dd>
          {fmtDate(b.created_at)}
          {b.created_by_name ? ` von ${b.created_by_name}` : ''}
        </dd>
        {b.result_note && (
          <>
            <dt>Ergebnis</dt>
            <dd>{b.result_note}</dd>
          </>
        )}
      </dl>
      {open && !editing && <BuyerMatches requestId={b.id} />}
      {editing && <ProfileForm b={b} busy={save.isPending} onSave={(body) => save.mutate(body)} onCancel={() => setEditing(false)} />}
      {closing && (
        <label className="field wide">
          <span>{closing === 'DONE' ? 'Ergebnis (z. B. Objekte angeboten, Besichtigung vereinbart)' : 'Warum kein Bedarf mehr?'}</span>
          <textarea value={result} maxLength={2000} rows={2} onChange={(e) => setResult(e.target.value)} autoFocus />
        </label>
      )}
      {canEdit && !editing && (
        <div className="form-actions">
          {closing ? (
            <>
              <button type="button" className="btn btn-small" disabled={save.isPending || !result.trim()} onClick={() => save.mutate({ status: closing, result_note: result.trim() })}>
                Speichern
              </button>
              <button type="button" className="btn btn-small btn-ghost" onClick={() => setClosing('')}>
                Abbrechen
              </button>
            </>
          ) : open ? (
            <>
              {b.status === 'OPEN' && (
                <button type="button" className="btn btn-small" disabled={save.isPending} onClick={() => save.mutate({ status: 'IN_PROGRESS' })}>
                  In Bearbeitung
                </button>
              )}
              <button type="button" className="btn btn-small" onClick={() => setClosing('DONE')}>
                Erledigt …
              </button>
              <button type="button" className="btn btn-small btn-ghost" onClick={() => setClosing('CANCELED')}>
                Kein Bedarf …
              </button>
              <button type="button" className="btn btn-small btn-ghost" onClick={() => setEditing(true)}>
                Suchprofil bearbeiten
              </button>
            </>
          ) : (
            <button type="button" className="btn btn-small btn-ghost" disabled={save.isPending} onClick={() => save.mutate({ status: 'IN_PROGRESS' })}>
              Wieder öffnen
            </button>
          )}
        </div>
      )}
      {save.isError && <MutationError error={save.error} />}
    </section>
  );
}

function ProfileForm({ b, busy, onSave, onCancel }: { b: BuyerRequest; busy: boolean; onSave: (body: BuyerRequestUpdate) => void; onCancel: () => void }) {
  const [types, setTypes] = useState<string[]>(b.property_types);
  const [purpose, setPurpose] = useState(b.purpose);
  const [location, setLocation] = useState(b.location);
  const num = (v: number | null | undefined) => (v == null ? '' : String(v));
  const [radius, setRadius] = useState(num(b.radius_km));
  const [areaMin, setAreaMin] = useState(num(b.living_area_min));
  const [areaMax, setAreaMax] = useState(num(b.living_area_max));
  const [rooms, setRooms] = useState(num(b.rooms_min));
  const [unitsMin, setUnitsMin] = useState(num(b.units_min));
  const [unitsMax, setUnitsMax] = useState(num(b.units_max));
  const [budget, setBudget] = useState(num(b.budget_max));
  const toInt = (v: string) => (v.trim() ? Math.max(0, Math.round(Number(v.replace(',', '.')))) : 0);
  const submit = () =>
    onSave({
      property_types: types as BuyerRequestUpdate['property_types'],
      purpose,
      location: location.trim(),
      radius_km: toInt(radius),
      living_area_min: toInt(areaMin),
      living_area_max: toInt(areaMax),
      rooms_min: rooms.trim() ? Number(rooms.replace(',', '.')) : 0,
      units_min: toInt(unitsMin),
      units_max: toInt(unitsMax),
      budget_max: toInt(budget),
    });
  return (
    <div className="buyer-profile-form">
      <fieldset className="chips" aria-label="Objektart">
        {Object.entries(propertyTypeLabels).map(([k, label]) => (
          <button
            key={k}
            type="button"
            className={types.includes(k) ? 'chip active' : 'chip'}
            aria-pressed={types.includes(k)}
            onClick={() => setTypes((t) => (t.includes(k) ? t.filter((x) => x !== k) : [...t, k]))}
          >
            {label}
          </button>
        ))}
      </fieldset>
      <div className="form-grid">
        <label className="field">
          <span>Zweck</span>
          <select value={purpose} onChange={(e) => setPurpose(e.target.value as BuyerRequest['purpose'])}>
            {Object.entries(purposeLabels).map(([k, label]) => (
              <option key={k} value={k}>
                {label}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>Ort</span>
          <input value={location} maxLength={200} onChange={(e) => setLocation(e.target.value)} />
        </label>
        <label className="field narrow">
          <span>Umkreis (km)</span>
          <input inputMode="numeric" value={radius} onChange={(e) => setRadius(e.target.value)} />
        </label>
        <label className="field narrow">
          <span>Wohnfläche ab (m²)</span>
          <input inputMode="numeric" value={areaMin} onChange={(e) => setAreaMin(e.target.value)} />
        </label>
        <label className="field narrow">
          <span>bis (m²)</span>
          <input inputMode="numeric" value={areaMax} onChange={(e) => setAreaMax(e.target.value)} />
        </label>
        <label className="field narrow">
          <span>Zimmer ab</span>
          <input inputMode="decimal" value={rooms} onChange={(e) => setRooms(e.target.value)} />
        </label>
        <label className="field narrow">
          <span>Einheiten ab</span>
          <input inputMode="numeric" value={unitsMin} onChange={(e) => setUnitsMin(e.target.value)} />
        </label>
        <label className="field narrow">
          <span>bis</span>
          <input inputMode="numeric" value={unitsMax} onChange={(e) => setUnitsMax(e.target.value)} />
        </label>
        <label className="field">
          <span>Budget bis (€)</span>
          <input inputMode="numeric" value={budget} onChange={(e) => setBudget(e.target.value)} />
        </label>
      </div>
      <div className="form-actions">
        <button type="button" className="btn btn-small" disabled={busy} onClick={submit}>
          Suchprofil speichern
        </button>
        <button type="button" className="btn btn-small btn-ghost" onClick={onCancel}>
          Abbrechen
        </button>
      </div>
    </div>
  );
}
